import { lazy, Suspense } from 'react';
import { Navigate, useRoutes, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCookies } from 'react-cookie';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';

// Layouts
import DashboardLayout from 'src/layouts/dashboard';
import EToolsLayout from 'src/layouts/e-tools';
import Header from 'src/layouts/dashboard/header';
import Main from 'src/layouts/dashboard/main';

// Components
import LoadingScreen from 'src/components/loading-screen';

// Pages
// import { Page404 } from 'src/pages/404';
// import { Page500 } from 'src/pages/500';

// ----------------------------------------------------------------------

const Dashboard = lazy(() => import('src/pages/dashboard'));
const Depo = lazy(() => import('src/pages/home'));
const History = lazy(() => import('src/pages/history'));
const Carriages = lazy(() => import('src/pages/carriages'));
const CarriagePassport = lazy(() => import('src/pages/carriagePassport'));
const CarriageAnalysys = lazy(() => import('src/pages/carriageAnalysys'));
const Inspections = lazy(() => import('src/pages/inspections'));
const Problems = lazy(() => import('src/pages/problems'));
const DirectionPlan = lazy(() => import('src/pages/directionPlan'));
const SinglePlan = lazy(() => import('src/pages/singlePlan'));
const ReportProblem = lazy(() => import('src/pages/reportProblem'));
const MapPage = lazy(() => import('src/pages/map'));
const InspectionSchedule = lazy(() => import('src/pages/inspectionSchedule'));
const ActivityLogs = lazy(() => import('src/pages/activityLogs'));
const InspectionTimeline = lazy(() => import('src/pages/inspectionTimeline'));
const Users = lazy(() => import('src/pages/users'));
const Organizations = lazy(() => import('src/pages/organizations'));
const TrainRecords = lazy(() => import('src/pages/trainRecords'));

const SelectProject = lazy(() => import('src/pages/select-project'));
const EToolsDashboard = lazy(() => import('src/pages/e-tools/dashboard'));
const EToolsInstruments = lazy(() => import('src/pages/e-tools/instruments'));
const EToolsBorrowRecords = lazy(() => import('src/pages/e-tools/borrow-records'));
const EToolsEmployees = lazy(() => import('src/pages/e-tools/employees'));

const Login = lazy(() => import('src/pages/login'));

// ----------------------------------------------------------------------

function SuspenseWrapper({ children }) {
  return <Suspense fallback={<LoadingScreen />}>{children}</Suspense>;
}

// Auth guard component
function AuthGuard({ children }) {
  const [cookies] = useCookies(['access']);

  if (!cookies.access) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// Full screen layout for xodim role
function FullScreenLayout({ children }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100vw',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Header onOpenNav={() => {}} isCollapsed={false} hideNavButton />
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
        }}
      >
        <Main isCollapsed={false}>{children}</Main>
      </Box>
    </Box>
  );
}

FullScreenLayout.propTypes = {
  children: PropTypes.node,
};

export default function Router() {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || 'uz';
  const [cookies] = useCookies(['access']);

  // Get user role from cookies
  const userRole = cookies?.user?.role;
  const isToolManager = !!cookies?.user?.tool_manager;

  // Post-login default landing
  const rootRedirect = (() => {
    if (isToolManager) return `/${currentLanguage}/e-tools/dashboard`;
    if (userRole === 'admin') return `/select-project`;
    if (userRole === 'xodim') return `/${currentLanguage}/direction-plan`;
    if (userRole === 'bort_injener') return `/${currentLanguage}/train-records`;
    return `/${currentLanguage}/dashboard`;
  })();

  // Define all possible route children
  const allRouteChildren = [
    {
      index: true,
      element: <Navigate to={`/${currentLanguage}/dashboard`} replace />,
    },
    {
      path: 'dashboard',
      element: <Dashboard />,
    },
    {
      path: 'depo',
      element: <Depo />,
    },
    {
      path: 'history',
      element: <History />,
    },
    {
      path: 'carriages',
      element: <Outlet />,
      children: [
        {
          index: true,
          element: <Carriages />,
        },
        {
          path: ':carriageId/passport',
          element: <CarriagePassport />,
        },
      ],
    },
    {
      path: 'carriage-analysys',
      element: <CarriageAnalysys />,
    },
    {
      path: 'inspections',
      element: <Inspections />,
    },
    {
      path: 'problems',
      element: <Problems />,
    },
    {
      path: 'map',
      element: <MapPage />,
    },
    {
      path: 'inspection-schedule',
      element: <InspectionSchedule />,
    },
    {
      path: 'activity-logs',
      element: <ActivityLogs />,
    },
    {
      path: 'inspection-timeline',
      element: <InspectionTimeline />,
    },
    {
      path: 'users',
      element: <Users />,
    },
    {
      path: 'organizations',
      element: <Organizations />,
    },
    {
      path: 'train-records',
      element: <TrainRecords />,
    },
    // Direction Plan routes
    {
      path: 'direction-plan',
      element: <Outlet />,
      children: [
        {
          index: true,
          element: <DirectionPlan />,
        },
        {
          path: 'view/:planId',
          element: <SinglePlan />,
        },
      ],
    },
  ];

  // Filter routes based on user role
  let filteredRouteChildren;
  let defaultRedirect;

  if (userRole === 'xodim') {
    // Xodim role: only direction-plan access, redirect to direction-plan
    filteredRouteChildren = allRouteChildren.filter((route) =>
      ['direction-plan'].includes(route.path || 'index')
    );
    defaultRedirect = `/${currentLanguage}/direction-plan`;
  } else if (userRole === 'bort_injener') {
    // Bort injener role: only train records access
    filteredRouteChildren = allRouteChildren.filter((route) =>
      ['train-records'].includes(route.path || 'index')
    );
    defaultRedirect = `/${currentLanguage}/train-records`;
  } else if (userRole === 'dispatcher') {
    // Dispatcher role: limited access
    filteredRouteChildren = allRouteChildren.filter((route) =>
      [
        'dashboard',
        'depo',
        'history',
        'carriages',
        'carriage-analysys',
        'direction-plan',
        'map',
      ].includes(route.path || 'index')
    );
    defaultRedirect = `/${currentLanguage}/dashboard`;
  } else if (userRole === 'texnolog') {
    // Texnolog role: limited access
    filteredRouteChildren = allRouteChildren.filter((route) =>
      ['problems', 'dashboard'].includes(route.path || 'index')
    );
    defaultRedirect = `/${currentLanguage}/dashboard`;
  } else {
    // Admin role: full access
    filteredRouteChildren = allRouteChildren;
    defaultRedirect = `/${currentLanguage}/dashboard`;
  }

  // Update the index route based on user role
  const updatedRouteChildren = filteredRouteChildren.map((route) => {
    if (route.index) {
      return {
        ...route,
        element: <Navigate to={defaultRedirect} replace />,
      };
    }
    return route;
  });

  const routes = [
    // Public routes
    {
      path: '/login',
      element: (
        <SuspenseWrapper>
          <Login />
        </SuspenseWrapper>
      ),
    },
    {
      path: '/report',
      element: (
        <SuspenseWrapper>
          <ReportProblem />
        </SuspenseWrapper>
      ),
    },

    // Language redirect
    {
      path: '/',
      element: <Navigate to={rootRedirect} replace />,
    },

    // Project selector (admin)
    {
      path: '/select-project',
      element: (
        <AuthGuard>
          <SuspenseWrapper>
            <SelectProject />
          </SuspenseWrapper>
        </AuthGuard>
      ),
    },

    // E-Tools protected routes
    {
      path: '/:lang/e-tools',
      element: (
        <AuthGuard>
          <EToolsLayout>
            <SuspenseWrapper>
              <Outlet />
            </SuspenseWrapper>
          </EToolsLayout>
        </AuthGuard>
      ),
      children: [
        { index: true, element: <Navigate to={`/${currentLanguage}/e-tools/dashboard`} replace /> },
        { path: 'dashboard', element: <EToolsDashboard /> },
        { path: 'instruments', element: <EToolsInstruments /> },
        { path: 'borrow-records', element: <EToolsBorrowRecords /> },
        { path: 'employees', element: <EToolsEmployees /> },
      ],
    },

    // Protected routes
    {
      path: '/:lang',
      element: (
        <AuthGuard>
          {userRole === 'xodim' || userRole === 'bort_injener' ? (
            <FullScreenLayout>
              <SuspenseWrapper>
                <Outlet />
              </SuspenseWrapper>
            </FullScreenLayout>
          ) : (
            <DashboardLayout>
              <SuspenseWrapper>
                <Outlet />
              </SuspenseWrapper>
            </DashboardLayout>
          )}
        </AuthGuard>
      ),
      children: updatedRouteChildren,
    },

    // Invalid language redirect
    {
      path: '/:lang/*',
      element: (
        <Navigate to={rootRedirect} replace />
      ),
    },

    // 404 redirect
    {
      path: '*',
      element: (
        <Navigate to={rootRedirect} replace />
      ),
    },
  ];

  return useRoutes(routes);
}
